/**
 * Панель фильтров каталога.
 *
 * Каждый выбор немедленно уходит в адресную строку: фильтр — это часть
 * ссылки, а не скрытое состояние компонента (docs/10-ui-ux.md).
 */

import { useSearchParams } from 'react-router'

import { TASK_STATUS_FILTERS } from '@/api/catalog'
import type { Difficulty, TaskFilters, Topic } from '@/api/catalog'
import { strings } from '@/lib/strings'

import { filtersToParams, hasAnyFilter, withFilter } from './filters'
import type { TaskStatusFilter } from './filters'

interface TaskFiltersBarProps {
  filters: TaskFilters
  topics: Topic[]
  tags: string[]
  canFilterByStatus: boolean
}

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']
const STATUSES: readonly TaskStatusFilter[] = TASK_STATUS_FILTERS
// Словарь, а не цепочка тернарников: с пятым значением она перестала бы
// читаться, а промах по подписи заметен только глазами.
const STATUS_LABELS: Record<TaskStatusFilter, string> = {
  solved: strings.catalog.status.solved,
  attempted: strings.catalog.status.attempted,
  unsolved: strings.catalog.status.notStarted,
  review: strings.catalog.status.review,
}

export function TaskFiltersBar({ filters, topics, tags, canFilterByStatus }: TaskFiltersBarProps) {
  const [, setParams] = useSearchParams()

  const update = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) => {
    setParams(filtersToParams(withFilter(filters, key, value)))
  }

  // Выбранная метка остаётся в списке, даже если текущая выборка сузилась
  // до задач с одной меткой: иначе фильтр невозможно было бы снять выбором.
  const tagOptions = [...new Set([...tags, ...(filters.tag ? [filters.tag] : [])])]

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface p-3">
      <Select
        id="filter-topic"
        label={strings.catalog.filters.topic}
        value={filters.topic ?? ''}
        placeholder={strings.catalog.filters.any}
        options={topics.map((topic) => ({ value: topic.slug, label: topic.title }))}
        onChange={(value) => {
          update('topic', value || undefined)
        }}
      />

      <Select
        id="filter-difficulty"
        label={strings.catalog.filters.difficulty}
        value={filters.difficulty ?? ''}
        placeholder={strings.catalog.filters.any}
        options={DIFFICULTIES.map((value) => ({
          value,
          label: strings.catalog.difficulty[value],
        }))}
        onChange={(value) => {
          update('difficulty', (value || undefined) as Difficulty | undefined)
        }}
      />

      <Select
        id="filter-status"
        label={strings.catalog.filters.status}
        value={filters.status ?? ''}
        placeholder={strings.catalog.filters.anyStatus}
        disabled={!canFilterByStatus}
        hint={canFilterByStatus ? undefined : strings.catalog.filters.statusRequiresLogin}
        options={STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
        onChange={(value) => {
          update('status', (value || undefined) as TaskFilters['status'])
        }}
      />

      {tagOptions.length > 0 && (
        <Select
          id="filter-tag"
          label={strings.catalog.filters.tag}
          value={filters.tag ?? ''}
          placeholder={strings.catalog.filters.any}
          options={tagOptions.map((tag) => ({ value: tag, label: tag }))}
          onChange={(value) => {
            update('tag', value || undefined)
          }}
        />
      )}

      {hasAnyFilter(filters) && (
        <button
          type="button"
          onClick={() => {
            setParams(new URLSearchParams())
          }}
          className="text-sm text-accent underline"
        >
          {strings.catalog.filters.reset}
        </button>
      )}
    </div>
  )
}

interface SelectProps {
  id: string
  label: string
  value: string
  placeholder: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  disabled?: boolean
  hint?: string | undefined
}

function Select({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
  disabled = false,
  hint,
}: SelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        title={hint}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className="rounded-lg border border-border bg-bg px-2 py-1.5 text-sm disabled:opacity-60"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}
